/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Download, RefreshCw, Search, X, AlertCircle, AlertTriangle, PackageOpen, Calendar, Shield,
} from "lucide-react";
import classNames from "classnames";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";

const STATUS_BADGE = {
    activo:   "badge-success",
    revocado: "badge-error",
    agotado:  "badge-warning",
};

const useDebounce = (value, delay) => {
    const [dv, setDv] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setDv(value), delay);
        return () => clearTimeout(h);
    }, [value, delay]);
    return dv;
};

// Mensajes por código de error de descarga del backend (`code` en la respuesta).
const DOWNLOAD_ERROR_MESSAGES = {
    file_missing:         "El archivo del paquete no está disponible ahora mismo. Se ha registrado una incidencia; inténtalo más tarde.",
    file_empty:           "El archivo del paquete está vacío. Se ha registrado una incidencia.",
    size_mismatch:        "El archivo no supera la verificación de integridad. Se ha registrado una incidencia.",
    entitlement_inactive: "Esta licencia no está activa (revocada o agotada).",
    package_not_approved: "El paquete todavía no está aprobado para descarga.",
    token_expired:        "El enlace de descarga ha caducado.",
    token_revoked:        "El enlace de descarga ha sido revocado.",
    token_exhausted:      "El enlace de descarga ha alcanzado su número máximo de usos.",
    unauthorized:         "No tienes acceso a esta descarga.",
};

/**
 * Con `responseType: "blob"`, el cuerpo de una respuesta de error también llega
 * como Blob, no como JSON parseado. Lo lee, lo parsea y traduce el `code` a un
 * mensaje legible (con respaldo a `detail`/`error` del backend).
 */
async function extractBlobError(err) {
    const fallback = "Error al iniciar la descarga.";
    const data = err?.response?.data;
    let payload = null;
    try {
        if (data instanceof Blob) {
            const text = await data.text();
            payload = text ? JSON.parse(text) : null;
        } else if (data && typeof data === "object") {
            payload = data;
        }
    } catch {
        payload = null;
    }
    if (payload) {
        return DOWNLOAD_ERROR_MESSAGES[payload.code] || payload.detail || payload.error || fallback;
    }
    return fallback;
}

const DOWNLOAD_OPTIONS = [
    { kind: "orca", label: "Orca", style: "btn-primary" },
    { kind: "prusa", label: "Prusa", style: "btn-secondary" },
    { kind: "zip", label: "ZIP completo", style: "btn-outline" },
];

const DownloadActions = ({ ent, onDownload, downloading, align = "end" }) => {
    const busy = downloading?.entitlementId === ent.id;
    return (
        <div
            className={`flex flex-wrap gap-1.5 ${align === "end" ? "justify-end" : "justify-start"}`}
            aria-label="Formatos de descarga"
        >
            {DOWNLOAD_OPTIONS.map((option) => {
                const optionBusy = busy && downloading?.kind === option.kind;
                return (
                    <button
                        key={option.kind}
                        className={`btn btn-xs gap-1 ${option.style}`}
                        onClick={() => onDownload(ent, option.kind)}
                        disabled={!ent.is_download_allowed || busy}
                        title={ent.is_download_allowed
                            ? `Descargar ${option.label}`
                            : "Descarga no disponible"}
                        aria-label={`Descargar ${ent.design_name || "modelo"} para ${option.label}`}
                    >
                        {optionBusy
                            ? <span className="loading loading-spinner loading-xs" />
                            : <Download className="w-3 h-3" />
                        }
                        <span>{option.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

const DownloadRow = ({ ent, onDownload, downloading }) => {
    // `revocation_message` solo llega del backend cuando `status === "revocado"`.
    // Un entitlement restaurado deja de traer el campo aunque exista historial
    // administrativo del motivo original.
    const revocationMessage = ent.status === "revocado" ? (ent.revocation_message || "") : "";

    return (
        <>
            <tr className={classNames("hover", ent.status === "revocado" && "opacity-90")}>
                <td>
                    <div className="font-medium text-sm leading-snug">
                        {ent.design_name || `Licencia #${ent.id}`}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-base-content/50 mt-0.5">
                        <Shield className="w-3 h-3 shrink-0" />
                        {ent.license_version ? `Licencia ${ent.license_version}` : "Licencia no disponible"}
                    </div>
                </td>
                <td className="text-sm whitespace-nowrap">
                    {new Date(ent.granted_at).toLocaleDateString("es-ES")}
                </td>
                <td className="text-sm text-center">
                    <span className="font-semibold">{ent.download_count}</span>
                    {ent.download_limit != null && (
                        <span className="text-base-content/50"> / {ent.download_limit}</span>
                    )}
                </td>
                <td>
                    <span className={`badge badge-sm ${STATUS_BADGE[ent.status] || "badge-ghost"}`}>
                        {ent.status_display || ent.status}
                    </span>
                </td>
                <td className="text-right">
                    <DownloadActions ent={ent} onDownload={onDownload} downloading={downloading} />
                </td>
            </tr>
            {ent.status === "revocado" && (
                <tr className="bg-transparent hover:bg-transparent">
                    <td colSpan={5} className="!p-0 !border-t-0">
                        <div
                            role="alert"
                            className="mx-0 mb-2 flex items-start gap-2 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error"
                        >
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                            <div className="min-w-0 leading-snug">
                                <span className="font-semibold">Descarga revocada</span>
                                {revocationMessage && (
                                    <>
                                        <span className="mx-1">:</span>
                                        <span className="break-words">{revocationMessage}</span>
                                    </>
                                )}
                                {!revocationMessage && (
                                    <span className="ml-1 text-error/80">
                                        (contacta con soporte si necesitas más información).
                                    </span>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const DownloadCard = ({ ent, onDownload, downloading }) => (
    <article className="rounded-xl border border-base-300 bg-base-200/40 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="font-medium text-sm leading-snug break-words">
                    {ent.design_name || `Licencia #${ent.id}`}
                </h3>
                <p className="flex items-center gap-1 text-xs text-base-content/50 mt-1">
                    <Shield className="w-3 h-3 shrink-0" />
                    {ent.license_version ? `Licencia ${ent.license_version}` : "Licencia no disponible"}
                </p>
            </div>
            <span className={`badge badge-sm shrink-0 ${STATUS_BADGE[ent.status] || "badge-ghost"}`}>
                {ent.status_display || ent.status}
            </span>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-xs">
            <div>
                <dt className="text-base-content/50">Concedido</dt>
                <dd className="mt-0.5">{new Date(ent.granted_at).toLocaleDateString("es-ES")}</dd>
            </div>
            <div>
                <dt className="text-base-content/50">Descargas</dt>
                <dd className="mt-0.5 font-semibold">
                    {ent.download_count}
                    {ent.download_limit != null && ` / ${ent.download_limit}`}
                </dd>
            </div>
        </dl>
        <DownloadActions ent={ent} onDownload={onDownload} downloading={downloading} align="start" />
        {ent.status === "revocado" && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">
                <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>{ent.revocation_message || "Descarga revocada. Contacta con soporte."}</span>
            </div>
        )}
    </article>
);

const MisDescargas = () => {
    const [entitlements, setEntitlements] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [searchTerm, setSearchTerm]     = useState("");
    const [sortDir, setSortDir]           = useState("desc");
    const [downloading, setDownloading]   = useState(null);
    const [dlError, setDlError]           = useState(null);

    const debouncedSearch = useDebounce(searchTerm, 300);

    const fetchEntitlements = useCallback(() => {
        setLoading(true);
        setError(null);
        axios
            .get("/digital-entitlements/", { withCredentials: true })
            .then((res) => setEntitlements(res.data.results || res.data || []))
            .catch((err) => setError(err.response?.data?.detail || "No se pudieron cargar las descargas."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchEntitlements(); }, [fetchEntitlements]);

    const handleDownload = useCallback(async (ent, kind) => {
        setDownloading({ entitlementId: ent.id, kind });
        setDlError(null);
        try {
            const res = await axios.post(
                `/digital-entitlements/${ent.id}/download/${kind}/`,
                {},
                { responseType: "blob", withCredentials: true },
            );
            const cd = res.headers["content-disposition"] || "";
            const match = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match
                ? match[1].replace(/['"]/g, "")
                : `${ent.design_name || "modelo"}.${kind === "zip" ? "zip" : "3mf"}`;
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            // Revocar tras un pequeño diferido: si se revoca de inmediato, algunos
            // navegadores cancelan la descarga que la etiqueta acaba de iniciar.
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            fetchEntitlements();
        } catch (err) {
            // Con responseType "blob" el cuerpo de error también llega como Blob,
            // así que `err.response.data` no es JSON: hay que leerlo y parsearlo.
            setDlError(await extractBlobError(err));
        } finally {
            setDownloading(null);
        }
    }, [fetchEntitlements]);

    const filtered = useMemo(() => {
        let list = [...entitlements];
        if (debouncedSearch) {
            const q = debouncedSearch.toLowerCase();
            list = list.filter(
                (e) =>
                    (e.design_name || "").toLowerCase().includes(q) ||
                    (e.status_display || e.status || "").toLowerCase().includes(q),
            );
        }
        list.sort((a, b) => {
            const dir = sortDir === "desc" ? -1 : 1;
            return dir * (new Date(a.granted_at) - new Date(b.granted_at));
        });
        return list;
    }, [entitlements, debouncedSearch, sortDir]);

    const showEmpty    = !loading && !error && filtered.length === 0;
    const centerContent = loading || showEmpty || !!error;

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">

            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="glassCard p-4 z-40">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <Download className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Mis descargas</h2>
                            <p className="text-sm text-base-content/60">
                                {entitlements.length}{" "}
                                {entitlements.length === 1 ? "licencia" : "licencias"}
                            </p>
                            <p className="text-xs text-base-content/50 mt-0.5">
                                Elige un 3MF preparado para tu laminador o descarga el paquete completo.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-sm btn-ghost btn-circle"
                            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                            title={sortDir === "desc" ? "Más recientes primero" : "Más antiguos primero"}
                            aria-label="Cambiar orden"
                        >
                            <Calendar className="w-4 h-4" />
                        </button>
                        <button
                            className="btn btn-sm btn-ghost btn-circle"
                            onClick={fetchEntitlements}
                            title="Actualizar"
                            disabled={loading}
                            aria-label="Actualizar lista"
                        >
                            {loading
                                ? <span className="loading loading-spinner loading-xs" />
                                : <RefreshCw className="w-4 h-4" />
                            }
                        </button>
                    </div>
                </div>

                <div className="flex flex-col-reverse lg:flex-row gap-2">
                    <div className="flex-1 min-w-0 lg:max-w-md">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            className="w-full"
                            placeholder="Buscar por diseño o estado…"
                        />
                    </div>
                </div>

                {debouncedSearch && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                            <Search className="w-3 h-3 shrink-0" />
                            <span className="max-w-[160px] truncate">&quot;{debouncedSearch}&quot;</span>
                            <button
                                onClick={() => setSearchTerm("")}
                                className="ml-0.5 hover:text-error transition-colors"
                                aria-label="Quitar filtro"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    </div>
                )}

                {dlError && (
                    <div role="alert" className="alert alert-error mt-3 py-2 text-sm">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{dlError}</span>
                        <button onClick={() => setDlError(null)} aria-label="Cerrar" className="ml-auto">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* ── Contenido ──────────────────────────────────────────────── */}
            <div className={classNames(
                "h-full flex-1 overflow-y-auto scrollbar_mod glassCard",
                centerContent ? "flex items-center justify-center" : "p-4",
            )}>
                {loading && (
                    <div className="text-center py-12">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                )}

                {error && !loading && (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                            <AlertCircle className="w-8 h-8 text-error" />
                        </div>
                        <h3 className="text-lg font-medium text-base-content mb-2">Error al cargar descargas</h3>
                        <p className="text-base-content/60 mb-4">{error}</p>
                        <button onClick={fetchEntitlements} className="btn btn-outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reintentar
                        </button>
                    </div>
                )}

                {showEmpty && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-6 bg-base-200 rounded-full flex items-center justify-center">
                            <PackageOpen className="w-10 h-10 text-base-content/40" />
                        </div>
                        {debouncedSearch ? (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">Sin resultados</h3>
                                <p className="text-base-content/60 mb-6">
                                    No hay descargas que coincidan con &quot;{debouncedSearch}&quot;
                                </p>
                                <button onClick={() => setSearchTerm("")} className="btn btn-outline">
                                    Limpiar búsqueda
                                </button>
                            </>
                        ) : (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    Aún no tienes descargas
                                </h3>
                                <p className="text-base-content/60">
                                    Cuando compres un producto digital, aparecerá aquí con su licencia.
                                </p>
                            </>
                        )}
                    </div>
                )}

                {!loading && !error && filtered.length > 0 && (
                    <>
                        <div className="md:hidden space-y-3" aria-label="Mis descargas">
                            {filtered.map((ent) => (
                                <DownloadCard
                                    key={ent.id}
                                    ent={ent}
                                    onDownload={handleDownload}
                                    downloading={downloading}
                                />
                            ))}
                        </div>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="table table-sm" aria-label="Mis descargas">
                                <thead>
                                    <tr className="text-xs uppercase">
                                        <th>Diseño</th>
                                        <th>Concedido</th>
                                        <th className="text-center">Descargas</th>
                                        <th>Estado</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((ent) => (
                                        <DownloadRow
                                            key={ent.id}
                                            ent={ent}
                                            onDownload={handleDownload}
                                            downloading={downloading}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MisDescargas;
